import type {
  CardPresentation,
  ImportedListingChange,
  ImportedListingContextSnapshot,
  ImportedListingPreviewResponse,
} from '@tcvs/shared';
import {
  IMPORTED_LISTING_CONTEXT_PREVIEW_VERSION,
  IMPORTED_LISTING_PRESENTATION_PREVIEW_VERSION,
  RAW_CARD_CONDITION_LABELS,
  parseBatchTransferPresentation,
} from '@tcvs/shared';

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const category = (value: unknown) => value === 'SPORT_SINGLES' || value === 'NON_SPORT_SINGLES';
const price = (value: unknown) =>
  record(value) &&
  value.currency === 'USD' &&
  Number.isSafeInteger(value.amountMinor) &&
  (value.amountMinor as number) > 0;
function jsonValue(value: unknown, depth = 0): boolean {
  if (depth > 64) return false;
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every((item) => jsonValue(item, depth + 1));
  return record(value) && Object.values(value).every((item) => jsonValue(item, depth + 1));
}
function context(value: unknown): value is ImportedListingContextSnapshot {
  return (
    record(value) &&
    value.identitySource === 'CHECKLIST' &&
    typeof value.checklistId === 'string' &&
    value.checklistId.trim() !== '' &&
    record(value.identity) &&
    ['category', 'set', 'number', 'name'].every(
      (key) => typeof (value.identity as Record<string, unknown>)[key] === 'string',
    ) &&
    jsonValue(value.identity)
  );
}
function presentation(value: unknown): boolean {
  return parseBatchTransferPresentation(value).ok;
}
function supportedChange(value: unknown, version2: boolean, includePresentation: boolean): boolean {
  if (!record(value)) return false;
  switch (value.field) {
    case 'presentation':
      return (
        includePresentation &&
        record(value.before) &&
        Object.keys(value.before).length === 2 &&
        Object.prototype.hasOwnProperty.call(value.before, 'card') &&
        Object.prototype.hasOwnProperty.call(value.before, 'listing') &&
        (value.before.card === null || presentation(value.before.card)) &&
        (value.before.listing === null || presentation(value.before.listing)) &&
        presentation(value.after)
      );
    case 'title':
    case 'description':
      return (
        (value.before === null || typeof value.before === 'string') &&
        typeof value.after === 'string'
      );
    case 'askingPrice':
      return (value.before === null || price(value.before)) && price(value.after);
    case 'features':
      return (
        (value.before === null ||
          (Array.isArray(value.before) &&
            value.before.every((item) => typeof item === 'string'))) &&
        Array.isArray(value.after) &&
        value.after.every((item) => typeof item === 'string')
      );
    case 'listingCategory':
      return version2 && (value.before === null || category(value.before)) && category(value.after);
    case 'listingContext':
      return (
        version2 &&
        context(value.before) &&
        context(value.after) &&
        value.before.checklistId === value.after.checklistId
      );
    default:
      return false;
  }
}
/** An omitted echo is a legacy server, whose original four change types remain usable. */
export function supportsImportedListingPreview(
  value: ImportedListingPreviewResponse,
  requestedPresentation = false,
): boolean {
  if (!record(value) || !Array.isArray(value.results)) return false;
  const version3 = value.previewVersion === IMPORTED_LISTING_PRESENTATION_PREVIEW_VERSION;
  const version2 = value.previewVersion === IMPORTED_LISTING_CONTEXT_PREVIEW_VERSION;
  if (!version3 && !version2 && value.previewVersion !== undefined) return false;
  if (version3 ? typeof value.includePresentation !== 'boolean' : 'includePresentation' in value)
    return false;
  if (
    requestedPresentation
      ? !version3 || value.includePresentation !== true
      : version3 && value.includePresentation !== false
  )
    return false;
  return value.results.every(
    (row) =>
      record(row) &&
      ['READY', 'UNCHANGED', 'BLOCKED'].includes(row.status) &&
      Array.isArray(row.reasons) &&
      row.reasons.every((reason) => typeof reason === 'string') &&
      Array.isArray(row.changes) &&
      row.changes.every((change) =>
        supportedChange(
          change,
          version2 || version3,
          version3 && value.includePresentation === true,
        ),
      ) &&
      new Set(row.changes.map((change) => change.field)).size === row.changes.length,
  );
}

const identityLabels: Record<string, string> = {
  category: 'Category',
  set: 'Set',
  number: 'Card number',
  name: 'Player / card name',
  year: 'Year',
  brand: 'Brand',
  subset: 'Subset',
  attributes: 'Attributes',
  rookie: 'Rookie',
  autograph: 'Autograph',
  memorabilia: 'Memorabilia',
  serialNumbered: 'Serial numbered',
  team: 'Team',
  language: 'Language',
  variation: 'Parallel / variation',
  serialTo: 'Print run',
  serialNumber: 'Observed serial number',
};
function valueText(value: unknown): string {
  if (value === undefined) return 'Not recorded';
  if (value === null) return 'Empty (null)';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return value === '' ? 'Empty text' : value;
  return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
}
function stable(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  if (record(value))
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stable(value[key])}`)
      .join(',')}}`;
  return JSON.stringify(value);
}
function ContextDetails({
  before,
  after,
}: {
  before: ImportedListingContextSnapshot;
  after: ImportedListingContextSnapshot;
}) {
  const beforeIdentity = before.identity as unknown as Record<string, unknown>;
  const afterIdentity = after.identity as unknown as Record<string, unknown>;
  const allKeys = new Set([...Object.keys(beforeIdentity), ...Object.keys(afterIdentity)]);
  const orderedKeys = [
    ...Object.keys(identityLabels).filter((key) => allKeys.has(key)),
    ...[...allKeys]
      .filter((key) => !Object.prototype.hasOwnProperty.call(identityLabels, key))
      .sort(),
  ];
  const rows: { id: string; label: string; before: unknown; after: unknown }[] = [
    {
      id: 'source',
      label: 'Identity source',
      before: before.identitySource === 'CHECKLIST' ? 'Checklist' : before.identitySource,
      after: after.identitySource === 'CHECKLIST' ? 'Checklist' : after.identitySource,
    },
    {
      id: 'checklist',
      label: 'Checklist reference',
      before: before.checklistId,
      after: after.checklistId,
    },
    ...orderedKeys.map((key) => ({
      id: `identity:${key}`,
      label: Object.prototype.hasOwnProperty.call(identityLabels, key)
        ? identityLabels[key]!
        : `${key} (additional field)`,
      before: Object.prototype.hasOwnProperty.call(beforeIdentity, key)
        ? beforeIdentity[key]
        : undefined,
      after: Object.prototype.hasOwnProperty.call(afterIdentity, key)
        ? afterIdentity[key]
        : undefined,
    })),
  ];
  return (
    <div>
      <strong>Listing draft details</strong>
      <p>
        Refresh the saved listing draft to the current confirmed card details. The card’s identity
        is unchanged.
      </p>
      <table aria-label="Listing draft details comparison">
        <thead>
          <tr>
            <th scope="col">Field</th>
            <th scope="col">Saved listing details</th>
            <th scope="col">Current confirmed card details</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <th scope="row">
                {row.label}
                {stable(row.before) !== stable(row.after) ? ' (changed)' : ''}
              </th>
              <td style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {valueText(row.before)}
              </td>
              <td style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {valueText(row.after)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
const categoryLabel = (value: unknown) =>
  value === null
    ? 'Not saved'
    : value === 'SPORT_SINGLES'
      ? 'Sport trading cards'
      : value === 'NON_SPORT_SINGLES'
        ? 'Non-sport trading cards'
        : 'Unsupported category';
function PresentationDetails({ value }: { value: CardPresentation | null }) {
  if (value === null) return <span>Not recorded</span>;
  return value.kind === 'RAW' ? (
    <span>Raw · {RAW_CARD_CONDITION_LABELS[value.condition]}</span>
  ) : (
    <span>
      Graded · Grader: {value.grader} · Grade: {value.grade} · Certification:{' '}
      {value.certificationNumber ?? 'Not recorded'}
    </span>
  );
}
/** The field discriminant selects the renderer; context objects can never become prices. */
export function ImportedListingChangeDetails({ change }: { change: ImportedListingChange }) {
  switch (change.field) {
    case 'presentation':
      return (
        <div>
          <strong>Condition / slab information replacement</strong>
          <div>
            Current saved card: <PresentationDetails value={change.before.card} />
          </div>
          <div>
            Current saved listing draft: <PresentationDetails value={change.before.listing} />
          </div>
          <div>
            Imported replacement for both: <PresentationDetails value={change.after} />
          </div>
          <p>
            This replaces the displayed card and listing conditions. The vision grade is unchanged.
          </p>
        </div>
      );
    case 'listingContext':
      return <ContextDetails before={change.before} after={change.after} />;
    case 'listingCategory':
      return (
        <div>
          <strong>Listing category</strong>
          <div>Current: {categoryLabel(change.before)}</div>
          <div>Proposed: {categoryLabel(change.after)}</div>
        </div>
      );
    case 'title':
    case 'description':
      return (
        <div>
          <strong>{change.field === 'title' ? 'Title' : 'Description'}</strong>
          <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            Current: {change.before === null ? 'Not saved' : change.before || 'Empty'}
          </div>
          <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            Imported: {change.after || 'Empty'}
          </div>
        </div>
      );
    case 'askingPrice': {
      const format = (value: typeof change.before) =>
        value === null ? 'Not saved' : `${value.currency} ${(value.amountMinor / 100).toFixed(2)}`;
      return (
        <div>
          <strong>Asking price</strong>
          <div>Current: {format(change.before)}</div>
          <div>Imported: {format(change.after)}</div>
        </div>
      );
    }
    case 'features': {
      const format = (value: typeof change.before) =>
        value === null ? 'Not saved' : value.length ? value.join(', ') : 'None';
      return (
        <div>
          <strong>Features</strong>
          <div>Current: {format(change.before)}</div>
          <div>Imported: {format(change.after)}</div>
        </div>
      );
    }
  }
}
